import { Box, Button, Field, Flex, Heading, Skeleton, Stack, Text, Textarea } from '@chakra-ui/react'
import { MESSAGE_MAX } from '@ipc/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api } from '~/api/client'
import { keys } from '~/api/hooks'
import { ErrorAlert, RetryError, useFriendlyError } from '~/components/member/feedback'
import { FormStack } from '~/components/member/layout'
import { useT } from '~/i18n/use-t'

/** "Write to us": the member's messages and the replies, and a box to send one. */
export function ContactForm() {
  const { t, config } = useT()
  const friendly = useFriendlyError()
  const qc = useQueryClient()
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)
  const list = useQuery({ queryKey: keys.messages, queryFn: () => api('memberMessages') })
  const send = useMutation({
    mutationFn: (text: string) => api('memberMessageSend', { body: { body: text } }),
    onSuccess: (_sent, text) => {
      // Clear the box only if it still holds what was sent: text typed while the message was on its way stays.
      setBody((current) => (current.trim() === text ? '' : current))
      void qc.invalidateQueries({ queryKey: keys.messages })
    },
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!body.trim()) return setError(t('contact.err'))
    setError(null)
    if (!send.isPending) send.mutate(body.trim())
  }
  // Newest first, under the box: a long thread never pushes the box to write in out of reach (mobile review).
  const items = [...(list.data?.items ?? [])].reverse()
  return (
    <Stack gap={4} as='section' aria-labelledby='contact-title'>
      <Heading as='h1' id='contact-title' size={{ base: '2xl', md: '3xl' }}>
        {t('contact.title')}
      </Heading>
      <FormStack gap={3} onSubmit={submit} noValidate>
        <Field.Root invalid={Boolean(error)}>
          <Field.Label>{t('contact.label')}</Field.Label>
          <Textarea value={body} maxLength={MESSAGE_MAX} rows={4} onChange={(e) => setBody(e.target.value)} />
          <Field.HelperText>{t('contact.left', { n: MESSAGE_MAX - body.length })}</Field.HelperText>
          <Field.ErrorText>{error}</Field.ErrorText>
        </Field.Root>
        {send.isError && <ErrorAlert>{friendly(send.error)}</ErrorAlert>}
        {send.isSuccess && body === '' && <Text role='status'>{t('contact.sent')}</Text>}
        <Button type='submit' size='lg' alignSelf={{ base: 'stretch', sm: 'flex-start' }} loading={send.isPending}>
          {t('contact.send')}
        </Button>
      </FormStack>
      <Heading as='h2' size='lg' mt={4}>
        {t('contact.thread')}
      </Heading>
      {list.isPending && <Skeleton height='4.5rem' borderRadius='md' />}
      {list.isError && <RetryError error={list.error} onRetry={() => void list.refetch()} />}
      {list.data?.items.length === 0 && <Text color='fg.muted'>{t('contact.empty')}</Text>}
      <Stack gap={3} as='ul' listStyleType='none' m={0} p={0} hidden={!items.length}>
        {items.map((m) => {
          const mine = m.direction === 'member'
          return (
            <Box
              as='li'
              key={m.id}
              borderRadius='md'
              p={3}
              bg={mine ? 'brand.subtle' : 'bg.muted'}
              ms={mine ? { base: 6, md: 16 } : 0}
              me={mine ? 0 : { base: 6, md: 16 }}
              minW={0}
            >
              <Flex justify='space-between' gap={3} wrap='wrap' mb={1}>
                <Text fontWeight='bold'>{mine ? t('contact.you') : config.org.name}</Text>
                <Text asChild color='fg.muted'>
                  <time dateTime={m.createdAt}>
                    {new Date(m.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                  </time>
                </Text>
              </Flex>
              <Text whiteSpace='pre-wrap' overflowWrap='anywhere'>
                {m.body}
              </Text>
            </Box>
          )
        })}
      </Stack>
    </Stack>
  )
}
